import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Flex,
  Text,
} from "@chakra-ui/react";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { RouterLink } from "src/components/Links/RouterLink";
import { WithConfig } from "src/components/Permissions/WithConfig";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import { formatMoney, maybePluralize } from "src/services/format";
import * as Url from "src/services/url";

export const PendingPayments = () => {
  const organization = useOrganization();
  const remoteData = useRemoteDataRestQuery({
    enabled: organization.hasData(),
    queryKey: ["GetPendingPaymentsForMe", organization.toNullable()?.id],
    queryFn: () =>
      FSR.getPaymentsForMe({
        query: {
          organizationId: organization.toNullable()?.id ?? "",
          paymentStatus: "pending",
        },
      }).then((resp) => resp.data),
  });
  return (
    <WithConfig configType="Payments">
      <RemoteDataView
        remoteData={remoteData}
        error={() => <GenericError title="Unable to load requested payment" />}
      >
        {({ data: pendingPayments }) => {
          if (pendingPayments.length <= 0) {
            return null;
          }

          let totalDue = 0;
          const paymentNameCounts = new Map<string, number>();
          for (const payment of pendingPayments) {
            totalDue += payment.amountInCents;
            paymentNameCounts.set(
              payment.name,
              (paymentNameCounts.get(payment.name) ?? 0) + 1
            );
          }

          return (
            <Alert borderRadius="md" status="info" alignItems="flex-start">
              <AlertIcon />
              <Flex direction="column">
                <AlertTitle>
                  You have {pendingPayments.length} payment{" "}
                  {maybePluralize(
                    pendingPayments.length,
                    "request",
                    "requests"
                  )}
                </AlertTitle>
                <AlertDescription
                  display="flex"
                  gap="2"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Flex direction="column">
                    <Text>
                      {Array.from(paymentNameCounts)
                        .map(([name, count]) =>
                          count > 1 ? `${name} (x${count})` : name
                        )
                        .join(" · ")}
                    </Text>
                    <Text>
                      Total due{" "}
                      {formatMoney(
                        totalDue,
                        pendingPayments[0]?.currency ?? "usd"
                      )}
                    </Text>
                  </Flex>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="blackAlpha"
                    color="gray.700"
                    borderColor="blackAlpha.300"
                    as={RouterLink}
                    to={organization
                      .map(Url.Parent.Payment.index)
                      .withDefault("#")}
                  >
                    Review and pay
                  </Button>
                </AlertDescription>
              </Flex>
            </Alert>
          );
        }}
      </RemoteDataView>
    </WithConfig>
  );
};
